import { formatDate } from '@angular/common';
import { Component, inject, input, model, viewChild } from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { CellValueChangedEvent, ColDef, FilterModel, GridOptions } from 'ag-grid-community';
import { Dictionary } from 'lodash';
import { tryGetErrorMessage } from '../../../../error.utils';
import { defaultTableDefOptions } from '../../../../helpers';
import { BranchService } from '../../../../service/branch.service';
import { NotifyService } from '../../../../service/notify.service';
import { BranchDetailLocationItem, BranchDetailsResponse, LocationCamera } from '../../../../types';
import { CellInstallerDashboardLocationActionsComponent } from '../cell-installer-dashboard-location-actions/cell-installer-dashboard-location-actions.component';
import { CellInstallerDashboardLocationCamerasComponent } from '../cell-installer-dashboard-location-cameras/cell-installer-dashboard-location-cameras.component';

@Component({
  selector: 'app-installer-dashboard-table-locations',
  standalone: false,
  templateUrl: './installer-dashboard-table-locations.component.html',
  styleUrl: './installer-dashboard-table-locations.component.scss',
})
export class InstallerDashboardTableLocationsComponent {
  private branchService = inject(BranchService);
  private notifyService = inject(NotifyService);

  public branchId = input.required<number>();
  public branchCamerasByLocationId = input.required<Dictionary<LocationCamera[]>>();
  public branchLocations = model.required<BranchDetailsResponse['locations']>();

  public readonly locationGrid = viewChild<AgGridAngular>('locationGrid');

  readonly presetFilters: Array<{ label: string; filters: FilterModel }> = [
    {
      label: 'No Cameras',
      filters: {
        camera_status: {
          filterType: 'number',
          type: 'equals',
          filter: 0,
        },
      },
    },
    {
      label: 'No Transactions',
      filters: {
        most_recent_transaction: {
          filterType: 'text',
          type: 'contains',
          filter: '-',
        },
      },
    },
    {
      label: 'Has Disconnected Cameras',
      filters: {
        camera_disconnected: {
          filterType: 'number',
          type: 'greaterThan',
          filter: 0,
        },
      },
    },
    { label: 'Reset', filters: {} },
  ];

  public readonly locationColumns: ColDef[] = [
    {
      field: 'name',
      headerName: 'Location Name',
      cellDataType: 'text',
      minWidth: 200,
      filter: 'agTextColumnFilter',
      sortable: true,
      sort: 'asc',
      tooltipField: 'name',
    },
    {
      field: 'camera_status',
      headerName: 'Camera Status',
      cellDataType: 'number',
      minWidth: 150,
      sortable: true,
      filter: 'agNumberColumnFilter',
      cellRenderer: CellInstallerDashboardLocationCamerasComponent,
      cellRendererParams: {
        allCameras: () => this.branchCamerasByLocationId(),
      },
      valueGetter: (p) => {
        return this.branchCamerasByLocationId()[p.data.id]?.length ?? 0;
      },
    },
    {
      field: 'camera_disconnected',
      headerName: '# Disconnected',
      cellDataType: 'number',
      minWidth: 150,
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueGetter: (p) => {
        return this.branchCamerasByLocationId()[p.data.id]?.filter((c) => c.status !== 'GREEN').length;
      },
    },
    {
      field: 'most_recent_transaction',
      headerName: 'Most Recent Transaction',
      cellDataType: 'text',
      minWidth: 250,
      filter: 'agTextColumnFilter',
      sortable: true,
      valueGetter: (p) => {
        const mostRecent = this.getMostRecentTxDate(p.data);
        if (!mostRecent) return '-';

        return formatDate(mostRecent, 'mediumDate', 'en-US');
      },
    },
    {
      field: 'is_national_account',
      headerName: 'National Account?',
      cellDataType: 'boolean',
      editable: true,
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      sortable: true,
      filter: 'agSetColumnFilter',
      onCellValueChanged: (event: CellValueChangedEvent<BranchDetailLocationItem>) => this.updateNationalAccountAttribute(event),
    },
    {
      field: 'actions',
      headerName: '',
      suppressColumnsToolPanel: true,
      suppressKeyboardEvent: () => true,
      sortable: false,
      width: 70,
      maxWidth: 70,
      cellClass: ['inline-flex', 'items-center', 'justify-end'],
      cellRenderer: CellInstallerDashboardLocationActionsComponent,
      cellRendererParams: {
        branchId: () => this.branchId(),
      },
    },
  ];

  public readonly locationTable: GridOptions<BranchDetailLocationItem> = {
    ...defaultTableDefOptions(),
    singleClickEdit: true,
    pagination: true,
    paginationPageSize: 20,
    paginationPageSizeSelector: false,
  };

  getMostRecentTxDate(location: BranchDetailLocationItem) {
    return location.most_recent_transaction_time;
  }

  setFilterModel(model: FilterModel) {
    this.locationGrid()?.api?.setFilterModel(model);
  }

  private updateNationalAccountAttribute(event: CellValueChangedEvent<BranchDetailLocationItem>) {
    this.branchService.updateBranchLocationAttribute(this.branchId(), event.data.id, 'is_national_account', !!event.newValue).subscribe({
      next: () => this.notifyService.notify('National Account status updated successfully'),
      error: (error) => {
        event.node.setDataValue('is_national_account', event.oldValue);
        this.notifyService.notify(tryGetErrorMessage(error));
      },
    });
  }
}
